import { v4 as uuidv4 } from 'uuid';

import { TeamCoverageEntry } from './coverageHoursTeamUtils';
import { minutesToTimeIso } from './coverageHoursTimeUtils';
import { toDayCodes } from './coverageHoursRowFormat';
import { CoverageHoursEntry, CoverageHoursPageInfo } from './useCoverageHours';

export const COVERAGE_HOURS_PAIR_WINDOW = 500;

export type CoverageHoursPairTarget = {
  category: string;
  issue: string | null;
};

export type CoverageHoursPair = {
  routingId: string;
  teams: TeamCoverageEntry[];
};

export type PairLoadProgress = {
  entries: CoverageHoursEntry[];
  routingsRead: number;
  reads: number;
};

export type PairLoadStep =
  | { kind: 'read'; start: number }
  | { kind: 'done'; pair: CoverageHoursPair }
  | { kind: 'failed' };

// searchCoverageHours pages over routings: pageInfo's start, total and perPage
// all count routings, and a routing carries any number of windows. Taking the
// page a routing at a time keeps the walk's own offset in that same unit.
export type PairLoadPage = {
  routings: CoverageHoursEntry[][];
  pageInfo: CoverageHoursPageInfo | null;
};

const FAILED: PairLoadStep = { kind: 'failed' };

function matchesTarget(
  entry: CoverageHoursEntry,
  target: CoverageHoursPairTarget
): boolean {
  return entry.category === target.category && entry.issue === target.issue;
}

type AssignedEntry = CoverageHoursEntry & { teamId: string };

const isAssigned = (entry: CoverageHoursEntry): entry is AssignedEntry =>
  Boolean(entry.teamId);

function buildPair(entries: CoverageHoursEntry[]): CoverageHoursPair | null {
  const routingIds = new Set(entries.map(entry => entry.routingId));
  const assigned = entries.filter(isAssigned);

  // A pair spread over two routings cannot be submitted back: the update input
  // names one routing, so saving would move the other routing's windows.
  if (routingIds.size !== 1 || assigned.length !== entries.length) {
    return null;
  }

  const teams = new Map<string, TeamCoverageEntry>();

  assigned.forEach(entry => {
    const team = teams.get(entry.teamId) ?? {
      id: uuidv4(),
      teamId: entry.teamId,
      teamLabel: entry.teamName ?? '',
      coverageBlocks: [],
    };

    team.coverageBlocks.push({
      daysOfWeek: toDayCodes(entry.daysOfWeek),
      startTime: minutesToTimeIso(entry.startMinute),
      endTime: minutesToTimeIso(entry.endMinute),
    });
    teams.set(entry.teamId, team);
  });

  return { routingId: [...routingIds][0], teams: [...teams.values()] };
}

export function startPairLoad(): PairLoadProgress {
  return { entries: [], routingsRead: 0, reads: 0 };
}

export function advancePairLoad(
  previous: PairLoadProgress,
  target: CoverageHoursPairTarget,
  page: PairLoadPage
): { progress: PairLoadProgress; step: PairLoadStep } {
  const blocks = page.routings.flat();
  const progress: PairLoadProgress = {
    entries: [
      ...previous.entries,
      ...blocks.filter(entry => matchesTarget(entry, target)),
    ],
    routingsRead: previous.routingsRead + page.routings.length,
    reads: previous.reads + 1,
  };

  if (page.routings.length === 0) {
    const pair = buildPair(progress.entries);

    return { progress, step: pair ? { kind: 'done', pair } : FAILED };
  }

  const lastBlock = blocks[blocks.length - 1];
  const total = page.pageInfo?.total ?? progress.routingsRead;
  const runContinues =
    progress.routingsRead < total &&
    (progress.entries.length === 0 ||
      (lastBlock !== undefined && matchesTarget(lastBlock, target)));

  if (!runContinues) {
    const pair = buildPair(progress.entries);

    return { progress, step: pair ? { kind: 'done', pair } : FAILED };
  }

  const perPage = page.pageInfo?.perPage ?? page.routings.length;
  const maxReads = Math.ceil(total / Math.max(perPage, 1)) + 1;

  if (progress.reads >= maxReads) {
    return { progress, step: FAILED };
  }

  return { progress, step: { kind: 'read', start: progress.routingsRead } };
}
